import { useCallback } from 'react';
import { asChatText } from '../utils/streamingReplyDisplay';

function useCitations() {
  /**
   * Scrolls to a message element and highlights it
   * @param {string} messageId - The ID of the message to scroll to
   */
  const scrollToMessage = useCallback((messageId) => {
    const element = document.getElementById(messageId);
    if (element) {
      // Scroll to the element with smooth behavior
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Add highlight class
      element.classList.add('message-highlight');

      // Remove highlight after animation
      setTimeout(() => {
        element.classList.remove('message-highlight');
      }, 2000);
    }
  }, []);

  /**
   * Parses content with citations and returns JSX elements
   * @param {string} content - The content string with [messageId] patterns
   * @param {Object} citationMap - Map of messageId to citation data
   * @returns {Array} - Array of React elements (text and citation links)
   */
  const renderWithCitations = useCallback((content, citationMap = {}) => {
    const text = asChatText(content);
    if (!text) return [];

    // Pattern to match [messageId] where messageId is alphanumeric
    const citationPattern = /\[([a-zA-Z0-9]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = citationPattern.exec(text)) !== null) {
      const beforeText = text.slice(lastIndex, match.index);
      const messageId = match[1];

      // Add text before citation
      if (beforeText) {
        parts.push({
          type: 'text',
          content: beforeText,
          key: `text-${lastIndex}`
        });
      }

      // Add citation
      parts.push({
        type: 'citation',
        messageId: messageId,
        citationData: citationMap[messageId],
        key: `citation-${match.index}`
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex),
        key: `text-${lastIndex}`
      });
    }

    return parts;
  }, []);

  return {
    scrollToMessage,
    renderWithCitations
  };
}

export default useCitations;
